import { useEffect, useRef } from 'react';

const WRAPPER_ID = 'gtranslate_wrapper_root';

// Global reference to wrapper to prevent losing it
let globalWrapper = null;
let isMoving = false; // Flag to prevent recursion
let scriptLoaded = false; // Flag to prevent reloading the script

const getOrCreateWrapper = () => {
    // First check the global reference
    if (globalWrapper && document.body.contains(globalWrapper)) {
        return globalWrapper;
    }
    
    // Search in DOM
    let el = document.getElementById(WRAPPER_ID);
    if (el) {
        globalWrapper = el;
        return el;
    }
    
    // Create new one
    el = document.createElement('div');
    el.className = 'gtranslate_wrapper';
    el.id = WRAPPER_ID;
    document.body.appendChild(el);
    globalWrapper = el;
    return el;
};

const moveWrapperToSlot = () => {
    if (isMoving) return false;
    
    const slotEl = document.getElementById('language-menu-item');
    const wrapper = getOrCreateWrapper();
    
    if (slotEl && wrapper) {
        if (!slotEl.contains(wrapper)) {
            isMoving = true;
            slotEl.appendChild(wrapper);
            isMoving = false;
        }
        // Always ensure wrapper is visible when moved to slot
        wrapper.style.display = '';
        return true;
    }
    return false;
};

export const TranslateWidget = () => {
    const initRef = useRef(false);

    // Load gtranslate script once
    useEffect(() => {
        // Check if script was already loaded (globally)
        if (scriptLoaded || document.querySelector('script[src*="gtranslate.net"]')) {
            return;
        }
        
        if (initRef.current) return;
        initRef.current = true;
        scriptLoaded = true;

        const wrapper = getOrCreateWrapper();

        window.gtranslateSettings = {
            default_language: "en",
            native_language_names: true,
            wrapper_selector: `#${WRAPPER_ID}`,
            switcher_horizontal_position: "right",
            switcher_vertical_position: "top",
            float_switcher_open_direction: "bottom"
        };

        const script = document.createElement('script');
        script.src = 'https://cdn.gtranslate.net/widgets/latest/popup.js';
        script.defer = true;
        document.body.appendChild(script);
    }, []);

    // Periodic check for slot and move wrapper
    useEffect(() => {
        // Try immediately
        moveWrapperToSlot();

        // Start interval for checking (as fallback)
        const intervalId = setInterval(() => {
            moveWrapperToSlot();
        }, 1000);

        // MutationObserver to rescue wrapper when parent is removed
        const observer = new MutationObserver((mutations) => {
            if (isMoving) return;
            
            let needsMove = false;
            
            for (const mutation of mutations) {
                for (const removed of mutation.removedNodes) {
                    if (removed.nodeType !== Node.ELEMENT_NODE) continue;
                    
                    // If removed element contains our wrapper
                    if (removed.id === WRAPPER_ID || removed.querySelector?.(`#${WRAPPER_ID}`)) {
                        // Wrapper was removed — restore from global reference
                        if (globalWrapper && !document.body.contains(globalWrapper)) {
                            isMoving = true;
                            document.body.appendChild(globalWrapper);
                            isMoving = false;
                        }
                        needsMove = true;
                    }
                }
                
                // Check if new language-menu-item appeared
                for (const added of mutation.addedNodes) {
                    if (added.nodeType !== Node.ELEMENT_NODE) continue;
                    if (added.id === 'language-menu-item' || added.querySelector?.('#language-menu-item')) {
                        needsMove = true;
                    }
                }
            }
            
            if (needsMove) {
                // Small delay to let DOM stabilize
                setTimeout(moveWrapperToSlot, 50);
            }
        });
        
        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            clearInterval(intervalId);
            observer.disconnect();
        };
    }, []);

    return null;
};
